import React, { FC, memo } from 'react';
import { Trans } from 'react-i18next';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getLoggedUserData, useGetUserByIdQuery, useRole, UserRoles } from 'entities/User';
import { Link, useParams } from 'react-router-dom';
import { InfoTag } from 'shared/ui/InfoTag';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { useGetCurrencySymbol } from 'app/appState';
import { AppRoutes } from 'app/config/routerConfig/types';

import { AccountDetailsForm } from './AccountDetailsForm';
import { AddressForm } from './AddressForm';
import { LatestActivity } from './LatestActivity';
import { PersonalInvoices } from './PersonalAccounts';
import { CustomerDetailsForm } from './CustomerDetailsForm';

import { AccessDetailsForm } from './AccessDetailsForm';
import { DebtDetailsForm } from './DebtDetailsForm';
import { UserBalanceHistory } from './UserBalanceHistory';

export const ProfilePage: FC = memo(() => {
  const { t } = useAppTranslation('profile');

  const { userId } = useParams();
  const { data } = useGetUserByIdQuery({ userId, params: { warehouses: true, userDebt: true } });

  const loggedUser = useAppSelector(getLoggedUserData);

  const { isManagement } = useRole();

  const loggedUserEmail = loggedUser?.email;

  const currencySymbol = useGetCurrencySymbol();

  if (!userId) {
    return <div>{t('User not exists')}</div>;
  }

  if (!data) {
    return <div>{t('User not authorized')}</div>;
  }

  const {
    firstName,
    lastName,
    email,
    phone,
    dateOfBirth,
    country,
    city,
    street,
    zip,
    house,
    apartment,
    balance,
    language,
    identificationNumber,
    additionalEmail,
    company,
    accountType,
    bankingRequisites,
    role,
    permissions,
    warehouses,
    userDebt,
    isInDebtCollection,
    debtCollectionNote,
    additionalPhone,
    accountingNumber,
    disposalContentStatus,
  } = data;

  return (
    <div className="pt-7">
      <Trans
        t={t}
        i18nKey="<0>Profile</0> <1>{{firstName}} {{lastName}}</1>"
        components={[
          <span key="0" className="text-2xl font-semibold" />,
          <Link key="1" className="text-2xl font-semibold text-accent" to={isManagement ? `${AppRoutes.USERS}/${userId}/contracts` : ''} />,
        ]}
        values={{ firstName, lastName }}
      />
      <div className="flex gap-3 text-accent items-center">
        {t('Balance: {{balance}}', { balance: balance ?? 0 })} {currencySymbol}
        <UserBalanceHistory userId={userId} balance={balance ?? 0} />
      </div>
      <div className="flex space-x-20 mt-10">
        <div className="basis-2/3">
          <InfoTag className="w-full">
            {t('If you want to edit information that is not available to you - contact an administrator')}
          </InfoTag>

          <AccountDetailsForm
            userId={userId}
            firstName={firstName}
            lastName={lastName}
            email={email}
            phone={phone}
            balance={balance}
            accountType={accountType}
            company={company}
            role={role}
            identificationNumber={identificationNumber}
            accountingNumber={accountingNumber}
          />
          {(role === UserRoles.MANAGER || role === UserRoles.STAFF || role === UserRoles.PARTNER) && (
            <AccessDetailsForm userId={userId} role={role} permissions={permissions} assignedWarehouses={warehouses} />
          )}
          <DebtDetailsForm
            userId={userId}
            userDebt={userDebt}
            isInDebtCollection={isInDebtCollection}
            debtCollectionNote={debtCollectionNote}
            currencySymbol={currencySymbol}
            disposalContentStatus={disposalContentStatus}
          />
          <CustomerDetailsForm
            userId={userId}
            dateOfBirth={dateOfBirth}
            email={email}
            additionalEmail={additionalEmail}
            language={language}
            bankingRequisites={bankingRequisites}
            phone={phone}
            additionalPhone={additionalPhone}
          />
          <AddressForm userId={userId} house={house} zip={zip} apartment={apartment} street={street} city={city} country={country} />
        </div>
        <div className="flex flex-col basis-1/3 space-y-12">
          <LatestActivity email={email} loggedUserEmail={loggedUserEmail} />
          <PersonalInvoices userId={userId} />
        </div>
      </div>
    </div>
  );
});
